import { useAppDispatch } from "@/app/hooks";
import { updateLeaseComp as updateLeaseCompAction } from "@/features/survey/leaseCompsSlice";
import {
  leaseCompsApi,
  useUpdateLeaseCompMutation,
} from "@/services/leaseComps";
import { useCurrentLeaseComp } from "@/shared/hooks/useCurrentLeaseComp";
import { useLeaseCompFieldRules } from "@/shared/hooks/useLeaseCompFieldRules";
import LeaseCompDetailAddressForm from "@survey-components/leaseComps/LeaseCompDetailAddressForm";
import type { LeaseComp } from "@survey-types/survey.leaseComp.types";
import { useEffect, useState } from "react";
import LeaseCompDetailHeaderForm from "./LeaseCompDetailHeaderForm";
import LeaseCompPhotoUpload from "./LeaseCompPhotoUpload";

const LeaseCompDetailForm = () => {
  const dispatch = useAppDispatch();
  const currentLeaseCompFromRedux = useCurrentLeaseComp();
  const fieldRules = useLeaseCompFieldRules();
  const [updateLeaseComp] = useUpdateLeaseCompMutation();

  // Local state for editable form data
  const [leaseComp, setLeaseComp] = useState(currentLeaseCompFromRedux);

  // Sync Redux data to local state when lease comp changes
  useEffect(() => {
    setLeaseComp(currentLeaseCompFromRedux);
  }, [currentLeaseCompFromRedux]);

  if (!leaseComp) {
    return null;
  }

  const handleAddressUpdate = (_id: string, updates: Partial<LeaseComp>) => {
    if (leaseComp) {
      // Use functional updater to avoid stale closure when autofill fires
      // multiple onChange events simultaneously (one per field).
      // Deep-merge leaseCompFields so each partial update accumulates correctly.
      setLeaseComp((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          ...updates,
          leaseCompFields: updates.leaseCompFields
            ? { ...prev.leaseCompFields, ...updates.leaseCompFields }
            : prev.leaseCompFields,
        };
      });

      dispatch(
        updateLeaseCompAction({
          leaseCompId: leaseComp.leaseCompId,
          updates,
        }),
      );

      // Update RTK Query cache directly so every component reading from
      // useGetLeaseCompsQuery also sees the latest data without a refetch.
      const patchResult = dispatch(
        leaseCompsApi.util.updateQueryData(
          "getLeaseComps",
          leaseComp.surveyId,
          (draft) => {
            const item = draft.find(
              (lc) => lc.leaseCompId === leaseComp.leaseCompId,
            );
            if (item) {
              // Deep-merge leaseCompFields BEFORE Object.assign so the spread
              // reads the original cached fields rather than a value already
              // overwritten by Object.assign (which would wipe city/state/zip
              // when only propertyAddress is being saved, causing the
              // comparison section to show empty fields).
              if (updates.leaseCompFields) {
                item.leaseCompFields = {
                  ...item.leaseCompFields,
                  ...updates.leaseCompFields,
                };
              }
              // Apply any other top-level changes (name, isHidden, order…)
              // excluding leaseCompFields which was already deep-merged above.
              const { leaseCompFields: _ignored, ...topLevelUpdates } = updates;
              Object.assign(item, topLevelUpdates);
            }
          },
        ),
      );

      // Use RTK Query mutation to trigger survey timestamp update
      updateLeaseComp({
        leaseCompId: leaseComp.leaseCompId,
        updates,
        surveyId: leaseComp.surveyId,
      }).catch((_error) => {
        console.error("Failed to update lease comp");
        // Rollback RTK cache, Redux slice, and local state on failure.
        patchResult.undo();
        setLeaseComp(leaseComp);
        dispatch(
          updateLeaseCompAction({
            leaseCompId: leaseComp.leaseCompId,
            updates: { leaseCompFields: leaseComp.leaseCompFields },
          }),
        );
      });
    }
  };

  return (
    <div className="flex flex-col">
      {/* Sticky header — stays pinned to the top of the side-sheet scroll area */}
      <div className="sticky top-0 z-10 bg-white px-6 pt-6 pb-4 border-b border-slate-100">
        <LeaseCompDetailHeaderForm leaseComp={leaseComp} />
      </div>

      {/* Scrollable form content */}
      <div className="px-6 pt-6 pb-8 flex flex-col gap-y-6">
        {/* Address Information + Photo (between address and property info) */}
        {leaseComp.leaseCompFields && (
          <LeaseCompDetailAddressForm
            leaseComp={leaseComp}
            leaseCompId={leaseComp.leaseCompId}
            fieldRules={fieldRules}
            onUpdate={handleAddressUpdate}
            photoSection={
              <LeaseCompPhotoUpload ownerId={leaseComp.leaseCompId} />
            }
          />
        )}

        {/* System Fields (Read-only) */}
        {/* <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-md border border-slate-200">
        <div>
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">
            ID
          </div>
          <div className="text-sm text-slate-900 font-mono break-all">
            {leaseComp.leaseCompId}
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">
            Created By
          </div>
          <div className="text-sm text-slate-900">{leaseComp.createdBy}</div>
        </div>
        <div>
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">
            Created On
          </div>
          <div className="text-sm text-slate-900">
            {new Date(leaseComp.createdOn).toLocaleDateString()}
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">
            Last Updated
          </div>
          <div className="text-sm text-slate-900">
            {new Date(leaseComp.updatedOn).toLocaleDateString()}
          </div>
        </div>
      </div> */}
      </div>
    </div>
  );
};

export default LeaseCompDetailForm;
